<template>
  <div style="background-color:#4a5568; overflow:hidden;">
    <!-- Loader -->
    <v-container
      v-if="loading"
      class="fill-height d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate color="#ff3b47" size="64" />
    </v-container>

    <!-- Main content -->
    <div v-else>
      <!-- Welcome Header -->
      <v-container class="mb-5">
        <v-row class="text-center">
          <v-col cols="12">
            <!-- Back Button -->
            <div class="d-flex align-center justify-start mb-4">
              <v-btn 
                icon 
                class="back-button" 
                @click="confirmBackNavigation"
                style="background: rgba(255, 255, 255, 0.1); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 12px; color: white;"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
            </div>
            
            <v-card-title
              style="color:white; font-size:1.5rem;"
              class="font-weight-bold"
            >
            </v-card-title>
          </v-col>
        </v-row>
      </v-container>

      <!-- Main Content Area -->
      <div
        class="pa-4"
        style="background-color:white; border-radius:20px 20px 0 0; min-height:70%;"
      >
        <!-- Header Section -->
        <v-row class="d-flex justify-space-between align-center mb-4">
          <div
            class="text-h4 font-weight-bold mx-6 my-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:1px;
                   text-shadow:2px 2px 4px rgba(0,0,0,.2);"
          >
            OLD JOBS
          </div>
          
          <!-- Add Report Button -->
          <div v-if="canCreateReport" class="mx-6">
            <v-btn
              large
              @click="navigateToCreateReport"
              style="background: #4a5568; color: white; border-radius: 8px; text-transform: none;"
              class="font-weight-medium"
            >
              <v-icon left>mdi-plus</v-icon>
              Create Old Job
            </v-btn>
          </div>
        </v-row>

        <!-- Search & Filter Section -->
        <div class="mx-6 mb-6">
          <div
            class="text-h6 font-weight-bold mb-3"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:0.5px;"
          >
            Filter & Search
          </div>
          
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                dense 
                outlined
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search Job Number / Customer"
                hide-details
                style="border-radius: 8px;"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="filterStatus"
                :items="statusOptions"
                dense 
                outlined
                label="Filter by Status"
                prepend-inner-icon="mdi-filter"
                hide-details
                style="border-radius: 8px;"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Old Jobs Section -->
        <div class="mx-6">
          <div
            class="text-h6 font-weight-bold mb-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:0.5px;"
          >
            Inspection Reports
          </div>

          <!-- Cards Grid -->
          <div class="reports-grid">
            <div
              v-for="(job, index) in filteredJobs" 
              :key="job.id || index"
              class="report-card"
              @click="navigateToEditJob(job)"
            >
              <!-- Header Section -->
              <div class="report-card-header">
                <v-chip
                  :color="getStatusColor(job.status)"
                  dark
                  label
                  small
                  class="status-chip"
                >
                  {{ job.status || "Unknown" }}
                </v-chip>
                <!-- Info & Download Buttons -->
                <div class="header-actions">
                  <button class="info-btn" @click.stop="openInfoModal(job)" title="View Details">
                    <v-icon small>mdi-information</v-icon>
                  </button>
                  <button 
                    v-if="canDownloadReport(job)"
                    class="download-btn" 
                    @click.stop="downloadReport(job)"
                    title="Download PDF"
                  >
                    <v-icon small>mdi-download</v-icon>
                  </button>
                </div>
              </div>
              
              <!-- Job Number Badge -->
              <div class="px-4 pb-2">
                <v-chip
                  color="#4a5568"
                  dark
                  label
                  class="job-number-chip"
                >
                  Job #: {{ job.jobNumber || 'N/A' }}
                </v-chip>
              </div>
              
              <!-- Content Section -->
              <div class="report-card-content">
                <div class="info-row">
                  <span class="info-label">Customer:</span>
                  <span class="info-value">{{ job.customerName || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Seal Type:</span>
                  <span class="info-value">{{ job.jobName || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Engineer:</span>
                  <span class="info-value">{{ job.engineer?.fullName || 'N/A' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Inspection Date:</span>
                  <span class="info-value">{{ formatDate(job.currentDate) }}</span>
                </div>
              </div>
              
              <!-- Card Action -->
              <div class="report-card-actions">
                <v-btn
                  color="#4a5568"
                  dark
                  block
                  class="action-btn"
                  style="text-transform: none; border-radius: 6px;"
                >
                  <v-icon left small>mdi-file-document</v-icon>
                  View Old Job
                </v-btn>
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-if="filteredJobs.length === 0" class="text-center mt-8">
            <div class="no-results">
              <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-file-document-outline</v-icon>
              <h3 style="color: #4a5568; margin-bottom: 8px;">No old jobs found</h3>
              <p style="color: #7f8c8d;">Try adjusting your search or filter criteria</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Report View Dialog -->
    <v-dialog v-model="reportDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-app-bar dark color="#4a5568">
          <v-toolbar-title>Old Job Report - {{ currentJob.jobNumber }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            v-if="canDownloadReport(currentJob)"
            icon
            @click="downloadReportFromDialog(currentJob)"
            title="Download PDF"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click="reportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-app-bar>

        <v-card-text class="pa-6" id="report-content">
          <!-- Job Information Section -->
          <v-card class="mb-4 elevation-2">
            <v-card-title style="background: #4a5568; color: white;">
              Job Information
            </v-card-title>
            <v-card-text class="pt-4">
              <div class="modal-grid">
                <div class="modal-field">
                  <div class="modal-field-label">Job Number</div>
                  <div class="modal-field-value">{{ currentJob.jobNumber }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Seal Type</div>
                  <div class="modal-field-value">{{ currentJob.jobName }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Serial Number</div>
                  <div class="modal-field-value">{{ currentJob.serialNumber }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Customer</div>
                  <div class="modal-field-value">{{ currentJob.customerName }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Contact Person</div>
                  <div class="modal-field-value">{{ currentJob.customerNumber }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Status</div>
                  <div class="modal-field-value">
                    <span class="status-badge" :class="getStatusClass(currentJob.status)">
                      {{ currentJob.status }}
                    </span>
                  </div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Engineer</div>
                  <div class="modal-field-value">{{ currentJob.engineer?.fullName || 'N/A' }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Technician</div>
                  <div class="modal-field-value">{{ currentJob.technician?.fullName || 'N/A' }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Salesperson</div>
                  <div class="modal-field-value">{{ currentJob.salesperson?.fullName || 'N/A' }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Inspection Date</div>
                  <div class="modal-field-value">{{ currentJob.currentDate }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Shaft Size</div>
                  <div class="modal-field-value">{{ currentJob.ShaftSize }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Priority</div>
                  <div class="modal-field-value">
                    <v-chip :color="getPriorityColor(currentJob.urgency)" small dark>
                      {{ currentJob.urgency }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Inspection Checklist Section -->
          <v-card class="mb-4 elevation-2" v-if="checklist.length">
            <v-card-title style="background: #4a5568; color: white;">
              Inspection Checklist
            </v-card-title>
            <v-card-text class="pt-4">
              <div v-for="(section, sectionIndex) in checklist" :key="sectionIndex" class="mb-6">
                <h3 class="section-header mb-3" style="color: #4a5568;">
                  {{ section.sectionTitle }}
                </h3>
                <v-divider class="mb-3"></v-divider>
                
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left" style="width: 60%">Item</th>
                        <th class="text-left" style="width: 20%">Response</th>
                        <th class="text-left" style="width: 20%">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, itemIndex) in section.items" :key="itemIndex">
                        <td>{{ item.label }}</td>
                        <td>
                          <v-chip
                            :color="item.checked === 'Yes' ? 'green' : 'red'"
                            small
                            dark
                          >
                            {{ item.checked }}
                          </v-chip>
                        </td>
                        <td>
                          <div v-if="item.checked === 'Yes'" class="details-column">
                            <div v-if="item.seal1">{{ item.seal1 }}</div>
                            <div v-if="item.jobNumberField">Job #: {{ item.jobNumberField }}</div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <!-- Section Photos -->
                <div v-if="section.photos && section.photos.length" class="mt-4">
                  <h4 class="mb-2">Photos</h4>
                  <v-row>
                    <v-col
                      v-for="(photo, photoIndex) in section.photos"
                      :key="photoIndex"
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-img
                        :src="photo"
                        aspect-ratio="1"
                        class="grey lighten-2 clickable-image"
                        @click="openPhotoDialog(photo)"
                      >
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Job Description -->
          <v-card class="mb-4 elevation-2" v-if="currentJob.jobDescription">
            <v-card-title style="background: #4a5568; color: white;">
              Job Description
            </v-card-title>
            <v-card-text class="pt-4">
              <p>{{ currentJob.jobDescription }}</p>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Info Modal -->
    <v-dialog v-model="infoDialog.visible" max-width="700">
      <v-card style="border-radius: 12px;">
        <v-card-title style="background: #4a5568; color: white; border-radius: 12px 12px 0 0;">
          <span class="modal-title">Job #{{ infoDialog.job.jobNumber }} Details</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="infoDialog.visible = false" style="color: white;">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="modal-grid">
            <div class="modal-field">
              <div class="modal-field-label">Job Number</div>
              <div class="modal-field-value">{{ infoDialog.job.jobNumber || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Customer</div>
              <div class="modal-field-value">{{ infoDialog.job.customerName || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Seal Type</div>
              <div class="modal-field-value">{{ infoDialog.job.jobName || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Serial Number</div>
              <div class="modal-field-value">{{ infoDialog.job.serialNumber || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Engineer</div>
              <div class="modal-field-value">{{ infoDialog.job.engineer?.fullName || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Technician</div>
              <div class="modal-field-value">{{ infoDialog.job.technician?.fullName || 'N/A' }}</div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Status</div>
              <div class="modal-field-value">
                <span class="status-badge" :class="getStatusClass(infoDialog.job.status)">
                  {{ infoDialog.job.status || 'Unknown' }}
                </span>
              </div>
            </div>
            <div class="modal-field">
              <div class="modal-field-label">Inspection Date</div>
              <div class="modal-field-value">{{ infoDialog.job.currentDate || 'N/A' }}</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-btn 
            color="#4a5568" 
            dark 
            block 
            @click="copyModalContent(infoDialog.job)"
            style="text-transform: none; border-radius: 8px;"
          >
            Copy All Details
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Photo Viewer Dialog -->
    <v-dialog v-model="photoDialog" max-width="800px">
      <v-card style="border-radius: 12px;">
        <v-toolbar dark color="#4a5568">
          <v-toolbar-title>Photo</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="photoDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-img :src="selectedPhoto" max-height="600"></v-img>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog for Back Button -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card style="border-radius: 12px;">
        <v-card-title style="background: #4a5568; color: white; border-radius: 12px 12px 0 0;">
          Confirm Navigation
        </v-card-title>
        <v-card-text class="pa-6">
          Are you sure you want to go back?
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="backDialog = false" style="text-transform: none;">Cancel</v-btn>
          <v-btn color="#4a5568" dark @click="goBack" style="text-transform: none; border-radius: 6px;">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay :value="downloading">
      <v-progress-circular indeterminate size="70" color="#ff3b47"></v-progress-circular>
      <div class="mt-4 white--text">Generating PDF...</div>
    </v-overlay>

    <!-- Snackbar Notification -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  getDocFromServer,
  getDocFromCache
} from "firebase/firestore";
import { firebaseApp, auth } from "@/firebaseConfig"
import { onAuthStateChanged } from "firebase/auth";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const db = getFirestore(firebaseApp);

export default {
  name: "OldJobs",
  data() {
    return {
      loading: true,
      downloading: false,
      reportDialog: false,
      photoDialog: false,
      selectedPhoto: "",
      search: "",
      filterStatus: null,
      filterCustomer: null,
      jobs: [],
      customers: [],
      checklist: [],
      currentJob: {},
      backDialog: false,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      infoDialog: {
        visible: false,
        job: {}
      },
      statusOptions: [
        "All",
        "Inspection",
        "Costing Complete",
        "Quoted",
        "InProgress",
        "Ready",
        "Completed",
        "Delayed",
        "Canceled",
      ],
      userRole: "",
      userPermissions: {},
      loggedInUser: '',
      authStateUnsubscribe: null,
    };
  },

  computed: {
    canCreateReport() {
      return this.userPermissions?.oldJobs?.Add_Button || false;
    },
    filteredJobs() {
      return this.jobs.filter((job) => {
        const searchLower = this.search.toLowerCase();
        const matchesSearch = !this.search || 
          (job.jobNumber && job.jobNumber.toString().toLowerCase().includes(searchLower)) ||
          (job.customerName && job.customerName.toLowerCase().includes(searchLower));
        const matchesStatus = !this.filterStatus || this.filterStatus === "All" || job.status === this.filterStatus;
        const matchesCustomer = !this.filterCustomer || job.customerName === this.filterCustomer;
        return matchesSearch && matchesStatus && matchesCustomer;
      });
    },
  },

  async created() {
    this.authStateUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        this.$router.push('/login');
        return;
      }
      
      try {
        this.loggedInUser = user.email;
        await this.fetchUserPermissions(user.email);
        await Promise.all([
          this.fetchJobs(),
          this.fetchCustomers()
        ]);
        this.loading = false;
      } catch (e) {
        this.handleError(e, "Loading data");
        this.loading = false;
      }
    });
  },

  beforeDestroy() {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
    }
  },

  methods: {
    handleError(error, operation) {
      console.error(`Error in ${operation}:`, error);
      
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        this.$router.push('/login');
        return;
      }
      
      this.showSnackbar(`${operation} failed: ${error.message}`, "error");
    },

    async fetchUserPermissions(email) {
      try {
        const q = query(collection(db, 'users'), where('email', '==', email));
        
        let snap;
        try {
          snap = await getDocs(q);
        } catch (serverError) {
          console.log("Server unavailable for permissions, trying cache...");
          await new Promise(resolve => setTimeout(resolve, 500));
          snap = await getDocs(q);
        }
        
        if (!snap.empty) {
          const userData = snap.docs[0].data();
          this.userPermissions = userData || {};
          this.userRole = userData.role || '';
        }
      } catch (e) {
        this.handleError(e, "Loading user permissions");
      }
    },

    async fetchJobs() {
  try {
    let jobsSnapshot;
    
    try {
      jobsSnapshot = await getDocs(collection(db, "jobs"));
    } catch (serverError) {
      console.log("Server unavailable for jobs, retrying...");
      await new Promise(resolve => setTimeout(resolve, 1000));
      jobsSnapshot = await getDocs(collection(db, "jobs"));
    }
    
    this.jobs = jobsSnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      .sort((a, b) => {
        const jobNumA = parseInt(a.jobNumber) || 0;
        const jobNumB = parseInt(b.jobNumber) || 0;
        return jobNumB - jobNumA;
      });
  } catch (error) {
    console.error("Error fetching jobs:", error);
    this.showSnackbar("Failed to fetch jobs", "error");
  }
},

    async fetchCustomers() {
      try {
        const customersSnapshot = await getDocs(collection(db, "customers"));
        this.customers = customersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          companyName: doc.data()["Company Name"] || "",
        }));
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },

    async fetchChecklist(jobNumber) {
      try {
        const checklistRef = collection(db, "checklists");
        const q = query(checklistRef, where("jobNumber", "==", jobNumber));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          this.checklist = docData.checklist || [];
        } else {
          this.checklist = [];
        }
      } catch (error) {
        console.error("Error fetching checklist:", error);
        this.checklist = [];
      }
    },

    navigateToEditJob(job) {
      localStorage.setItem('selectedOldJobId', job.id);
      this.$router.push({ name: 'EditOldJob' });
    },

    navigateToCreateReport() {
      this.$router.push({ name: 'AddOldJobs' });
    },

    canDownloadReport(job) {
      const allowedStatuses = ["Costing Complete", "Delivered", "Ready"];
      return allowedStatuses.includes(job.status);
    },

    async downloadReport(job) {
      if (!this.canDownloadReport(job)) {
        this.showSnackbar("Report can only be downloaded for jobs with status: Costing Complete, Ready, or Delivered", "warning");
        return;
      }

      this.downloading = true;
      try {
        this.currentJob = job;
        await this.fetchChecklist(job.jobNumber);
        this.reportDialog = true;
        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const element = document.getElementById("report-content");
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`Old_Job_Report_${job.jobNumber}.pdf`);
        this.showSnackbar("PDF downloaded successfully");
        this.reportDialog = false;
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.showSnackbar("Failed to generate PDF", "error");
      } finally {
        this.downloading = false;
      }
    },

    async downloadReportFromDialog(job) {
      this.downloading = true;
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const element = document.getElementById("report-content");
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`Old_Job_Report_${job.jobNumber}.pdf`);
        this.showSnackbar("PDF downloaded successfully");
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.showSnackbar("Failed to generate PDF", "error");
      } finally {
        this.downloading = false;
      }
    },

    openPhotoDialog(photo) {
      this.selectedPhoto = photo;
      this.photoDialog = true;
    },

    openInfoModal(job) {
      this.infoDialog.job = job;
      this.infoDialog.visible = true;
    },

    getStatusColor(status) {
      const colors = {
        Inspection: "orange",
        "Costing Complete": "green",
        Quoted: "purple",
        InProgress: "blue",
        Ready: "cyan",
        Completed: "teal",
        Delayed: "red",
        Canceled: "red",
      };
      return colors[status] || "grey";
    },

    getStatusClass(status) {
      const classMap = {
        'Inspection': 'status-inspection',
        'Costing Complete': 'status-complete',
        'Quoted': 'status-quoted',
        'InProgress': 'status-inprogress',
        'Ready': 'status-ready',
        'Completed': 'status-complete',
        'Delayed': 'status-delayed',
        'Canceled': 'status-canceled'
      };

      return classMap[status] || 'status-default';
    },

    getPriorityColor(priority) {
      const colors = {
        High: "red",
        Medium: "orange",
        Low: "green",
      };
      return colors[priority] || "grey";
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? dateString : date.toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    confirmBackNavigation() {
      this.backDialog = true;
    },

    goBack() {
      this.backDialog = false;
      this.$router.go(-1);
    },

    showSnackbar(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    copyModalContent(job) {
      const content = `Job Number: ${job.jobNumber || 'N/A'}
Customer: ${job.customerName || 'N/A'}
Seal Type: ${job.jobName || 'N/A'}
Serial Number: ${job.serialNumber || 'N/A'}
Engineer: ${job.engineer?.fullName || 'N/A'}
Technician: ${job.technician?.fullName || 'N/A'}
Salesperson: ${job.salesperson?.fullName || 'N/A'}
Status: ${job.status || 'Unknown'}
Inspection Date: ${job.currentDate || 'N/A'}
Priority: ${job.urgency || 'N/A'}`;
      
      navigator.clipboard.writeText(content)
        .then(() => {
          this.showSnackbar("Job details copied successfully");
        })
        .catch(() => {
          this.showSnackbar("Failed to copy job details", "error");
        });
    }
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

body,
div,
span,
v-card-title,
v-row,
v-col,
v-card {
  font-family: "Montserrat", sans-serif;
}

.reports-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.report-card {
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e6ed;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  min-height: 320px;
  display: flex;
  flex-direction: column;
}

.report-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 8px;
  background: #4a5568;
}

.report-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.1);
  border-color: #ddd;
}

.report-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px 16px;
  margin-top: 8px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.info-btn,
.download-btn {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
}

.info-btn:hover,
.download-btn:hover {
  background: #edf2f7;
  transform: scale(1.1);
}

.download-btn {
  background: #48bb78;
  border-color: #48bb78;
  color: white;
}

.download-btn:hover {
  background: #38a169;
  border-color: #38a169;
}

.report-card-content {
  padding: 0 16px;
  flex: 1;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  align-items: flex-start;
}

.info-label {
  color: #4a5568;
  font-weight: 600;
  font-size: 13px;
  min-width: 100px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  color: #2d3748;
  font-size: 13px;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
}

.report-card-actions {
  padding: 16px;
  margin-top: auto;
}

.status-chip {
  font-weight: 600 !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.job-number-chip {
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 0.5px;
}

/* Status Classes */
.status-inspection { background: rgba(237, 137, 54, 0.2); color: #ed8936; border: 1px solid rgba(237, 137, 54, 0.3); }
.status-quoted { background: rgba(128, 90, 213, 0.2); color: #805ad5; border: 1px solid rgba(128, 90, 213, 0.3); }
.status-inprogress { background: rgba(66, 153, 225, 0.2); color: #4299e1; border: 1px solid rgba(66, 153, 225, 0.3); }
.status-ready { background: rgba(0, 188, 212, 0.2); color: #00bcd4; border: 1px solid rgba(0, 188, 212, 0.3); }
.status-complete { background: rgba(72, 187, 120, 0.2); color: #48bb78; border: 1px solid rgba(72, 187, 120, 0.3); }
.status-delivered { background: rgba(56, 161, 105, 0.2); color: #38a169; border: 1px solid rgba(56, 161, 105, 0.3); }
.status-canceled { background: rgba(245, 101, 101, 0.2); color: #f56565; border: 1px solid rgba(245, 101, 101, 0.3); }
.status-default { background: rgba(113, 128, 150, 0.2); color: #718096; border: 1px solid rgba(113, 128, 150, 0.3); }

/* Modal Styles */
.modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-field-label {
  color: #4a5568;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modal-field-value {
  color: #2d3748;
  font-size: 14px;
  word-break: break-word;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-block;
}

.no-results {
  color: #4a5568;
  padding: 40px 20px;
}

.section-header {
  font-size: 1.3rem;
  font-weight: 600;
  padding: 8px 0;
}

.details-column div {
  margin-bottom: 4px;
  font-size: 0.9rem;
}

.clickable-image {
  cursor: pointer;
  transition: transform 0.2s;
  border-radius: 8px;
}

.clickable-image:hover {
  transform: scale(1.05);
}

/* Responsive Design */
@media (max-width: 768px) {
  .reports-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-grid {
    grid-template-columns: 1fr;
  }
  
  .info-row {
    flex-direction: column;
    gap: 4px;
  }
  
  .info-label,
  .info-value {
    text-align: left;
    max-width: 100%;
  }
}

@media print {
  .v-app-bar,
  .v-btn {
    display: none !important;
  }
}
</style>